/**
 * Canonical e-card template media URL resolution (still vs MP4 vs poster).
 *
 * Animated templates store the playable blob on {@link CardTemplate.mediaToken}.
 * Never pass that token to {@code <img src>} — use {@link resolveEcardTemplateStillUrl}
 * or {@link EcardTemplateThumbnail} instead (same rules as
 * {@link TemplateSelectionTile} and {@link AdminEcardThumbnail}).
 */

import type { CardTemplate } from '../types';
import { getMediaUrl } from '../services/cardTemplatesService';
import { resolvePublicImageUrl } from '../services/publicTemplatesService';
import {
  buildTemplateMediaTokenUrl,
  resolvePreferredTemplateStillSrc,
  resolveTemplateVideoSrc,
} from './responsiveImage';

/** Minimal template fields for still / video URL resolution. */
export type EcardTemplateMediaSource = Pick<
  CardTemplate,
  | 'mediaToken'
  | 'mediaType'
  | 'isAnimated'
  | 'previewImageUrl'
  | 'posterMediaToken'
  | 'thumbnailTokens'
>;

/** How to pick among WebP thumbnail widths. */
export type EcardStillThumbPreference = 'tile' | 'poster';

/**
 * True when {@link CardTemplate.mediaToken} is the MP4 (or other video blob).
 */
export function isEcardVideoTemplate(
  source: EcardTemplateMediaSource,
): boolean {
  return source.mediaType === 'video' || source.isAnimated === true;
}

/**
 * Returns the best **still** media token for {@code <img>} / poster — never the MP4
 * {@link CardTemplate.mediaToken} on video templates.
 */
export function resolveEcardTemplateStillToken(
  source: EcardTemplateMediaSource,
  preference: EcardStillThumbPreference = 'tile',
): string | null {
  const thumbs = source.thumbnailTokens;
  let thumbToken: string | undefined;

  if (preference === 'poster') {
    thumbToken = thumbs?.w720 ?? thumbs?.w480 ?? thumbs?.w240;
  } else {
    thumbToken = thumbs?.w480 ?? thumbs?.w720 ?? thumbs?.w240;
  }

  if (thumbToken?.trim()) {
    return thumbToken;
  }

  if (source.posterMediaToken?.trim()) {
    return source.posterMediaToken;
  }

  if (!isEcardVideoTemplate(source) && source.mediaToken?.trim()) {
    return source.mediaToken;
  }

  return null;
}

function tokenToMediaUrl(token: string, mediaBaseUrl?: string): string {
  if (mediaBaseUrl) {
    return buildTemplateMediaTokenUrl(mediaBaseUrl, token);
  }

  return getMediaUrl(token);
}

/**
 * Absolute URL for a still suitable for {@code <img src>} (confirmation thumbs,
 * carousel tiles, admin lists). Pass {@link EcardTemplateMediaSource.previewImageUrl}
 * when no token exists. Optional {@link pendingFile} wins for user uploads.
 */
export function resolveEcardTemplateStillUrl(
  source: EcardTemplateMediaSource | null | undefined,
  options?: {
    mediaBaseUrl?: string;
    pendingFile?: File | null;
    thumbPreference?: EcardStillThumbPreference;
  },
): string | null {
  if (options?.pendingFile) {
    return URL.createObjectURL(options.pendingFile);
  }

  if (!source) {
    return null;
  }

  const token = resolveEcardTemplateStillToken(
    source,
    options?.thumbPreference ?? 'tile',
  );
  if (token) {
    return tokenToMediaUrl(token, options?.mediaBaseUrl);
  }

  if (source.previewImageUrl?.trim()) {
    return resolvePublicImageUrl(source.previewImageUrl);
  }

  return null;
}

/**
 * Absolute URL for the full animated asset (MP4). {@code null} for static templates.
 */
export function resolveEcardTemplateVideoUrl(
  source: EcardTemplateMediaSource | null | undefined,
  mediaBaseUrl?: string,
): string | null {
  if (!source || !isEcardVideoTemplate(source) || !source.mediaToken?.trim()) {
    return null;
  }

  if (mediaBaseUrl) {
    return resolveTemplateVideoSrc(source as CardTemplate, mediaBaseUrl);
  }

  return getMediaUrl(source.mediaToken);
}

/**
 * Poster still for {@code <video poster>} — largest WebP thumb, then JPEG poster token.
 */
export function resolveEcardTemplatePosterUrl(
  source: EcardTemplateMediaSource | null | undefined,
  mediaBaseUrl: string,
): string | null {
  if (!source) {
    return null;
  }

  const token = resolveEcardTemplateStillToken(source, 'poster');
  if (token) {
    return buildTemplateMediaTokenUrl(mediaBaseUrl, token);
  }

  return null;
}

/**
 * Carousel / grid tile still: WebP thumb, else non-video mediaToken, else previewImageUrl.
 * Matches legacy {@link TemplateSelectionTile} {@code resolveMediaUrl} behaviour.
 */
export function resolveEcardTemplateTileStillUrl(
  template: CardTemplate,
  mediaBaseUrl?: string,
): string | undefined {
  if (mediaBaseUrl != null) {
    const preferredStill = resolvePreferredTemplateStillSrc(template, mediaBaseUrl);
    if (preferredStill) {
      return preferredStill;
    }

    if (template.mediaType !== 'video' && template.mediaToken) {
      return buildTemplateMediaTokenUrl(mediaBaseUrl, template.mediaToken);
    }
  }

  return template.previewImageUrl || undefined;
}

/**
 * Carousel / grid tile video URL when {@link isEcardVideoTemplate} is true.
 */
export function resolveEcardTemplateTileVideoUrl(
  template: CardTemplate,
  mediaBaseUrl?: string,
): string | undefined {
  if (mediaBaseUrl == null) {
    return undefined;
  }

  return resolveTemplateVideoSrc(template, mediaBaseUrl) ?? undefined;
}

/**
 * Builds a minimal {@link EcardTemplateMediaSource} from admin list flat props.
 */
export function ecardMediaSourceFromAdminThumbnail(props: {
  mediaToken?: string | null;
  externalUrl?: string | null;
  isVideo?: boolean;
}): EcardTemplateMediaSource {
  return {
    mediaToken: props.mediaToken?.trim() ?? '',
    mediaType: props.isVideo ? 'video' : 'image',
    isAnimated: props.isVideo === true,
    previewImageUrl: props.externalUrl?.trim() || undefined,
    posterMediaToken: undefined,
    thumbnailTokens: undefined,
  };
}
